import { useState, type ReactNode } from "react";
import { Plus } from "lucide-react";
import { useNavigate, useParams } from "react-router";
import { Breadcrumb, Button, Loader, PageContainer, PageHeader } from "@repo/ui";
import {
  HistoriqueVersions,
  ListeScorecards,
  NouvelleScorecardDialog,
  ScorecardEditor,
} from "@repo/scorecard-editor";
import type { Id } from "@convex/_generated/dataModel";
import {
  aEntretienStructure,
  estGestionnaireClient,
  useCurrentUser,
} from "../lib/useCurrentUser";

function ReserveGestionnaires({ children }: { children: ReactNode }) {
  const user = useCurrentUser();
  if (user === undefined) {
    return (
      <PageContainer>
        <div className="flex justify-center p-16">
          <Loader />
        </div>
      </PageContainer>
    );
  }
  if (!estGestionnaireClient(user)) {
    return (
      <PageContainer>
        <PageHeader titre="Scorecards" />
        <p className="text-muted-foreground">
          La gestion des scorecards est réservée aux gestionnaires de
          l'organisation.
        </p>
      </PageContainer>
    );
  }
  if (!aEntretienStructure(user)) {
    return (
      <PageContainer>
        <PageHeader titre="Scorecards" />
        <p className="text-muted-foreground">
          Les scorecards servent à FairView, qui n'est pas ouvert
          à votre organisation. Contactez votre administrateur.
        </p>
      </PageContainer>
    );
  }
  return <PageContainer>{children}</PageContainer>;
}

function useScorecardId(): Id<"scorecards"> {
  const { id } = useParams<{ id: string }>();
  return id as Id<"scorecards">;
}

const lienScorecard = (id: Id<"scorecards">) => `/scorecards/${id}`;

export function ScorecardsListe() {
  const navigate = useNavigate();
  const [creation, setCreation] = useState(false);
  return (
    <ReserveGestionnaires>
      <PageHeader
        titre="Scorecards"
        sousTitre="Grilles d'entretien FairView de votre organisation. Une campagne reçoit la version publiée à son lancement, et la garde."
        actions={
          <Button onClick={() => setCreation(true)}>
            <Plus /> Nouvelle scorecard
          </Button>
        }
      />
      {creation && (
        <NouvelleScorecardDialog
          cible={{ type: "organisation" }}
          saisirSlug={false}
          onFermer={() => setCreation(false)}
          onCreee={(id) => void navigate(lienScorecard(id))}
        />
      )}
      <ListeScorecards
        portee={{ type: "organisation" }}
        lienScorecard={lienScorecard}
        onOuvrir={(id) => void navigate(lienScorecard(id))}
        messageVide="Aucune scorecard. Créez-en une, ou demandez à Evaluans de vous attribuer un modèle."
      />
    </ReserveGestionnaires>
  );
}

export function ScorecardEdition() {
  const scorecardId = useScorecardId();
  return (
    <ReserveGestionnaires>
      <Breadcrumb items={[{ label: "Scorecards", href: "/scorecards" }, { label: "Modifier" }]} />
      <ScorecardEditor
        key={scorecardId}
        scorecardId={scorecardId}
        lienHistorique={`${lienScorecard(scorecardId)}/historique`}
      />
    </ReserveGestionnaires>
  );
}

export function ScorecardHistorique() {
  const scorecardId = useScorecardId();
  return (
    <ReserveGestionnaires>
      <Breadcrumb
        items={[
          { label: "Scorecards", href: "/scorecards" },
          { label: "Modifier", href: lienScorecard(scorecardId) },
          { label: "Historique" },
        ]}
      />
      <HistoriqueVersions
        scorecardId={scorecardId}
        lienEditeur={lienScorecard(scorecardId)}
      />
    </ReserveGestionnaires>
  );
}
